import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  ExternalLink,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Save,
  Search,
  ShieldCheck,
  Trash2,
  X
} from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import { ADMIN_PERMISSIONS } from '../../constants';
import { adminAPI } from '../../services/api';
import useAuthStore from '../../store/authStore';
import { hasPermission } from '../../utils/rbac';

const audienceOptions = [
  { value: 'candidate', label: 'Candidate' },
  { value: 'employer', label: 'Employer' }
];

const documentTypeOptions = [
  { value: 'terms', label: 'Terms of Service', path: '/terms' },
  { value: 'privacy', label: 'Privacy Policy', path: '/privacy' }
];

const statusOptions = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' }
];

const emptyDraft = {
  id: null,
  audience: 'candidate',
  documentType: 'terms',
  title: 'Candidate Terms of Service',
  summary: '',
  content: '',
  status: 'draft',
  effectiveAt: '',
  metadata: {},
  version: null,
  publishedAt: null,
  updatedAt: null
};

const inputClass = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-100';
const labelClass = 'text-xs font-semibold uppercase tracking-[0.12em] text-slate-500';
const iconButtonClass = 'inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50';

const extractError = (error, fallback) => error?.response?.data?.error || error?.response?.error || error?.message || fallback;

const labelize = (value = '') => String(value || '')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase());

const formatDateTime = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not recorded';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const statusClass = (status) => ({
  published: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  draft: 'border-amber-200 bg-amber-50 text-amber-700',
  archived: 'border-slate-200 bg-slate-50 text-slate-600'
}[status] || 'border-slate-200 bg-slate-50 text-slate-600');

const documentTypeLabel = (value) => documentTypeOptions.find((item) => item.value === value)?.label || labelize(value);
const audienceLabel = (value) => audienceOptions.find((item) => item.value === value)?.label || labelize(value);

const defaultTitle = (audience, documentType) => `${audienceLabel(audience)} ${documentTypeLabel(documentType)}`;

const toDateInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
};

const isFutureDate = (value) => {
  if (!value) return false;
  const date = new Date(value);
  return !Number.isNaN(date.getTime()) && date.getTime() > Date.now();
};

const fromDocument = (document) => ({
  id: document.id,
  audience: document.audience,
  documentType: document.documentType,
  title: document.title || defaultTitle(document.audience, document.documentType),
  summary: document.summary || '',
  content: document.content || '',
  status: document.status || 'draft',
  effectiveAt: toDateInputValue(document.effectiveAt),
  metadata: document.metadata || {},
  version: document.version || null,
  publishedAt: document.publishedAt || null,
  updatedAt: document.updatedAt || null
});

const buildPayload = (draft, statusOverride) => ({
  audience: draft.audience,
  documentType: draft.documentType,
  title: draft.title.trim(),
  summary: draft.summary.trim(),
  content: draft.content.trim(),
  status: statusOverride || draft.status,
  effectiveAt: draft.effectiveAt || null,
  metadata: draft.metadata || {}
});

const publicUrl = (draft) => {
  const path = documentTypeOptions.find((item) => item.value === draft.documentType)?.path || '/terms';
  const host = draft.audience === 'employer' ? 'https://employer.sanspath.co.uk' : 'https://candidate.sanspath.co.uk';
  return `${host}${path}`;
};

const splitContent = (content) => String(content || '')
  .split(/\n{2,}/)
  .map((part) => part.trim())
  .filter(Boolean);

const Badge = ({ status }) => (
  <span className={`inline-flex items-center rounded-md border px-2 py-1 text-xs font-semibold ${statusClass(status)}`}>
    {labelize(status)}
  </span>
);

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className={labelClass}>{label}</span>
    <div className="mt-2">{children}</div>
    {hint && <span className="mt-1 block text-xs leading-5 text-slate-500">{hint}</span>}
  </label>
);

const PageSkeleton = () => (
  <div className="min-h-full px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
    <div className="mb-6 space-y-3">
      <SkeletonBlock className="h-8 w-64 rounded-lg" />
      <SkeletonBlock className="h-4 w-full max-w-2xl rounded-lg" />
    </div>
    <div className="grid gap-4 md:grid-cols-4">
      {[0, 1, 2, 3].map((item) => <SkeletonBlock key={item} className="h-28 rounded-lg" />)}
    </div>
    <SkeletonBlock className="mt-5 h-[30rem] rounded-lg" />
  </div>
);

const StatCard = ({ label, value, detail }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
    <p className={labelClass}>{label}</p>
    <p className="mt-2 text-2xl font-semibold text-slate-950">{value}</p>
    <p className="mt-1 text-xs leading-5 text-slate-500">{detail}</p>
  </div>
);

const EmptyState = ({ canManage, onCreate }) => (
  <div className="flex min-h-[20rem] flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-white p-8 text-center">
    <span className="flex h-12 w-12 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-600">
      <FileText size={22} />
    </span>
    <h2 className="mt-4 text-base font-semibold text-slate-950">No legal pages found</h2>
    <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">Create a text page for candidate or employer terms and privacy. Published pages appear immediately in the public apps.</p>
    {canManage && (
      <button
        type="button"
        onClick={onCreate}
        className="mt-5 inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white transition hover:bg-slate-800"
      >
        <Plus size={16} />
        New page
      </button>
    )}
  </div>
);

const LegalPageModal = ({
  canManage,
  draft,
  saving,
  deleting,
  onArchive,
  onClose,
  onDelete,
  onSave,
  onUpdate
}) => {
  if (!draft) return null;

  const paragraphs = splitContent(draft.content);
  const titleReady = draft.title.trim().length >= 3;
  const contentReady = draft.content.trim().length >= 20;
  const canSubmit = canManage && titleReady && contentReady && !saving && !deleting;
  const futureEffectiveAt = isFutureDate(draft.effectiveAt);
  const canSave = canSubmit && !(draft.status === 'published' && futureEffectiveAt);
  const canPublish = canSubmit && !futureEffectiveAt;
  const modeLabel = draft.id ? 'Edit legal page' : 'New legal page';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/50 px-3 py-4 backdrop-blur-sm sm:px-6 sm:py-8">
      <section
        role="dialog"
        aria-modal="true"
        aria-label={modeLabel}
        className="w-full max-w-6xl overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <p className={labelClass}>{draft.id ? `${audienceLabel(draft.audience)} / ${documentTypeLabel(draft.documentType)}` : 'Create text page'}</p>
            <h2 className="mt-1 text-xl font-semibold tracking-tight text-slate-950">{modeLabel}</h2>
          </div>
          <button type="button" onClick={onClose} disabled={saving || deleting} className={iconButtonClass} aria-label="Close legal page modal">
            <X size={18} />
          </button>
        </div>

        <div className="grid max-h-[calc(100vh-11rem)] overflow-y-auto lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="space-y-5 p-5">
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Audience">
                <select value={draft.audience} onChange={(event) => onUpdate('audience', event.target.value)} className={inputClass}>
                  {audienceOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </Field>
              <Field label="Page type">
                <select value={draft.documentType} onChange={(event) => onUpdate('documentType', event.target.value)} className={inputClass}>
                  {documentTypeOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select value={draft.status} onChange={(event) => onUpdate('status', event.target.value)} className={inputClass}>
                  {statusOptions.filter((item) => item.value !== 'all').map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </Field>
              <Field label="Effective from">
                <input
                  type="datetime-local"
                  value={draft.effectiveAt}
                  onChange={(event) => onUpdate('effectiveAt', event.target.value)}
                  className={inputClass}
                />
              </Field>
            </div>

            <Field label="Title">
              <input
                value={draft.title}
                onChange={(event) => onUpdate('title', event.target.value)}
                className={inputClass}
                placeholder="Candidate Terms of Service"
              />
            </Field>

            <Field label="Summary" hint="Optional internal/public summary. Keep it short and factual.">
              <textarea
                value={draft.summary}
                onChange={(event) => onUpdate('summary', event.target.value)}
                className={`${inputClass} min-h-[5rem] resize-y`}
                placeholder="Briefly describe the page or latest change."
              />
            </Field>

            <Field label="Page content" hint={`${draft.content.trim().length.toLocaleString('en-GB')} characters. Use blank lines to separate paragraphs.`}>
              <textarea
                value={draft.content}
                onChange={(event) => onUpdate('content', event.target.value)}
                className={`${inputClass} min-h-[24rem] resize-y font-mono text-[13px] leading-6`}
                placeholder="Write or paste the approved legal text here."
              />
            </Field>

            {(!titleReady || !contentReady) && (
              <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
                <AlertTriangle size={18} className="mt-0.5 shrink-0" />
                <span>Use a title of at least 3 characters and page content of at least 20 characters before saving.</span>
              </div>
            )}
            {futureEffectiveAt && (
              <div className="flex items-start gap-3 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm leading-6 text-sky-800">
                <AlertTriangle size={18} className="mt-0.5 shrink-0" />
                <span>Future effective dates must remain drafts. Publish this page when its effective date arrives so the current public page stays available.</span>
              </div>
            )}
          </div>

          <aside className="border-t border-slate-200 bg-slate-50 p-5 lg:border-l lg:border-t-0">
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between gap-3">
                <p className={labelClass}>Public target</p>
                <Badge status={draft.status} />
              </div>
              <a
                href={publicUrl(draft)}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex w-full items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
              >
                <span className="truncate">{publicUrl(draft)}</span>
                <ExternalLink size={16} className="shrink-0 text-slate-400" />
              </a>
            </div>

            <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
                <div>
                  <h3 className="text-sm font-semibold text-slate-950">Publishing rule</h3>
                  <p className="mt-1 text-sm leading-6 text-slate-500">Publishing archives the previous public page for the same audience and page type. Future-dated pages remain drafts until they take effect.</p>
                </div>
              </div>
            </div>

            <dl className="mt-4 rounded-lg border border-slate-200 bg-white p-4 text-sm">
              <div className="flex justify-between gap-4 border-b border-slate-100 pb-3">
                <dt className="text-slate-500">Version</dt>
                <dd className="font-semibold text-slate-900">{draft.version || 'New'}</dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-slate-100 py-3">
                <dt className="text-slate-500">Published</dt>
                <dd className="text-right font-semibold text-slate-900">{formatDateTime(draft.publishedAt)}</dd>
              </div>
              <div className="flex justify-between gap-4 pt-3">
                <dt className="text-slate-500">Updated</dt>
                <dd className="text-right font-semibold text-slate-900">{formatDateTime(draft.updatedAt)}</dd>
              </div>
            </dl>

            <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
              <p className={labelClass}>Preview</p>
              <h3 className="mt-3 text-base font-semibold text-slate-950">{draft.title || defaultTitle(draft.audience, draft.documentType)}</h3>
              {draft.summary && <p className="mt-2 text-sm leading-6 text-slate-600">{draft.summary}</p>}
              <div className="mt-4 max-h-56 space-y-3 overflow-y-auto pr-1">
                {paragraphs.slice(0, 4).map((paragraph, index) => (
                  <p key={`${index}-${paragraph.slice(0, 18)}`} className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-600">{paragraph}</p>
                ))}
                {!paragraphs.length && <p className="text-sm leading-6 text-slate-500">Preview appears as you write.</p>}
              </div>
            </div>
          </aside>
        </div>

        <div className="flex flex-col gap-2 border-t border-slate-200 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row">
            {draft.id && (
              <button
                type="button"
                onClick={onDelete}
                disabled={!canManage || deleting || saving}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-4 text-sm font-semibold text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                Delete
              </button>
            )}
            {draft.id && draft.status !== 'archived' && (
              <button
                type="button"
                onClick={onArchive}
                disabled={!canSubmit}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Archive size={16} />
                Archive
              </button>
            )}
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving || deleting}
              className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onSave()}
              disabled={!canSave}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Save
            </button>
            <button
              type="button"
              onClick={() => onSave('published')}
              disabled={!canPublish}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
              Publish
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

function LegalDocumentsPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const canManage = hasPermission(user, ADMIN_PERMISSIONS.CONFIG_MANAGE);
  const [filters, setFilters] = useState({
    audience: 'all',
    documentType: 'all',
    status: 'all',
    search: ''
  });
  const [draft, setDraft] = useState(null);

  const params = useMemo(() => ({
    ...(filters.audience !== 'all' ? { audience: filters.audience } : {}),
    ...(filters.documentType !== 'all' ? { documentType: filters.documentType } : {}),
    ...(filters.status !== 'all' ? { status: filters.status } : {}),
    ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
    limit: 100
  }), [filters]);

  const documentsQuery = useQuery(
    ['admin-legal-documents', params],
    () => adminAPI.getLegalDocuments(params),
    { keepPreviousData: true }
  );

  const documents = documentsQuery.data?.documents || [];
  const publishedCount = documents.filter((item) => item.status === 'published').length;
  const draftCount = documents.filter((item) => item.status === 'draft').length;
  const archivedCount = documents.filter((item) => item.status === 'archived').length;
  const latestUpdatedAt = documents[0]?.updatedAt;

  const saveMutation = useMutation(
    ({ id, payload }) => (id ? adminAPI.updateLegalDocument(id, payload) : adminAPI.createLegalDocument(payload)),
    {
      onSuccess: (data) => {
        toast.success(data.message || 'Legal page saved.');
        setDraft(null);
        queryClient.invalidateQueries('admin-legal-documents');
      },
      onError: (error) => toast.error(extractError(error, 'Legal page could not be saved.'))
    }
  );

  const deleteMutation = useMutation(
    (id) => adminAPI.deleteLegalDocument(id),
    {
      onSuccess: () => {
        toast.success('Legal page deleted.');
        setDraft(null);
        queryClient.invalidateQueries('admin-legal-documents');
      },
      onError: (error) => toast.error(extractError(error, 'Legal page could not be deleted.'))
    }
  );

  const closeModal = () => {
    if (!saveMutation.isLoading && !deleteMutation.isLoading) setDraft(null);
  };

  const updateDraft = (key, value) => {
    setDraft((current) => {
      if (!current) return current;
      const next = { ...current, [key]: value };
      if ((key === 'audience' || key === 'documentType') && !current.id) {
        next.title = defaultTitle(next.audience, next.documentType);
      }
      return next;
    });
  };

  const startNew = () => {
    const audience = filters.audience === 'all' ? 'candidate' : filters.audience;
    const documentType = filters.documentType === 'all' ? 'terms' : filters.documentType;
    setDraft({
      ...emptyDraft,
      audience,
      documentType,
      title: defaultTitle(audience, documentType)
    });
  };

  const editDocument = (document) => setDraft(fromDocument(document));

  const save = (statusOverride) => {
    if (!draft || !canManage || saveMutation.isLoading) return;

    if (draft.title.trim().length < 3) {
      toast.error('Add a title before saving.');
      return;
    }
    if (draft.content.trim().length < 20) {
      toast.error('Add at least 20 characters of page content.');
      return;
    }

    saveMutation.mutate({
      id: draft.id,
      payload: buildPayload(draft, statusOverride)
    });
  };

  const archive = () => {
    if (!draft?.id) return;
    save('archived');
  };

  const remove = () => {
    if (!draft?.id || deleteMutation.isLoading) return;
    if (!window.confirm('Delete this legal page? This removes it from admin lists and public display if it is published.')) return;
    deleteMutation.mutate(draft.id);
  };

  if (documentsQuery.isLoading && !documentsQuery.data) return <PageSkeleton />;

  return (
    <div className="min-h-full bg-[#f8f9fa] px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
      <PageHeader
        title="Legal Pages"
        description="Manage the text pages shown as Terms of Service and Privacy Policy in the employer and candidate apps."
        actions={(
          <button
            type="button"
            onClick={startNew}
            disabled={!canManage}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus size={16} />
            New page
          </button>
        )}
      />

      {!canManage && (
        <div className="mb-5 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <span>You can view legal pages, but editing requires configuration management permission.</span>
        </div>
      )}

      <section className="mb-5 grid gap-4 md:grid-cols-4">
        <StatCard label="Pages" value={documents.length} detail="Matching current filters" />
        <StatCard label="Published" value={publishedCount} detail="Visible in public apps" />
        <StatCard label="Drafts" value={draftCount} detail={`${archivedCount} archived`} />
        <StatCard label="Last update" value={latestUpdatedAt ? formatDateTime(latestUpdatedAt).split(',')[0] : 'None'} detail={latestUpdatedAt ? formatDateTime(latestUpdatedAt) : 'No changes yet'} />
      </section>

      <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-base font-semibold text-slate-950">Pages</h2>
              <p className="mt-1 text-sm leading-6 text-slate-500">Open a page to edit content, publish, archive, or delete.</p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:w-[42rem] lg:grid-cols-[minmax(0,1fr)_9rem_11rem_9rem]">
              <div className="relative sm:col-span-2 lg:col-span-1">
                <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <input
                  value={filters.search}
                  onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
                  className={`${inputClass} pl-9`}
                  placeholder="Search pages"
                />
              </div>
              <select value={filters.audience} onChange={(event) => setFilters((current) => ({ ...current, audience: event.target.value }))} className={inputClass}>
                <option value="all">All audiences</option>
                {audienceOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
              <select value={filters.documentType} onChange={(event) => setFilters((current) => ({ ...current, documentType: event.target.value }))} className={inputClass}>
                <option value="all">All page types</option>
                {documentTypeOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
              <select value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))} className={inputClass}>
                {statusOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </div>
          </div>
        </div>

        {documentsQuery.isFetching && (
          <div className="border-b border-slate-100 px-4 py-2 text-xs font-medium text-slate-500">Refreshing pages...</div>
        )}

        {documents.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Page</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Audience</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Status</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Version</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Updated</th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {documents.map((document) => (
                  <tr key={document.id} className="transition hover:bg-slate-50/80">
                    <td className="max-w-md px-4 py-4">
                      <div className="flex min-w-0 items-start gap-3">
                        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-600">
                          <FileText size={17} />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-950">{document.title}</p>
                          <p className="mt-1 text-xs text-slate-500">{documentTypeLabel(document.documentType)}</p>
                          {document.summary && <p className="mt-1 line-clamp-1 text-xs text-slate-400">{document.summary}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-slate-700">{audienceLabel(document.audience)}</td>
                    <td className="whitespace-nowrap px-4 py-4"><Badge status={document.status} /></td>
                    <td className="whitespace-nowrap px-4 py-4 text-sm text-slate-600">v{document.version}</td>
                    <td className="whitespace-nowrap px-4 py-4 text-sm text-slate-600">{formatDateTime(document.updatedAt)}</td>
                    <td className="px-4 py-4">
                      <div className="flex justify-end gap-2">
                        <a href={publicUrl(document)} target="_blank" rel="noreferrer" className={iconButtonClass} title="Open public page" aria-label={`Open ${document.title} public page`}>
                          <ExternalLink size={16} />
                        </a>
                        <button type="button" onClick={() => editDocument(document)} className={iconButtonClass} title="Edit page" aria-label={`Edit ${document.title}`}>
                          <Pencil size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-5">
            <EmptyState canManage={canManage} onCreate={startNew} />
          </div>
        )}
      </section>

      <LegalPageModal
        canManage={canManage}
        draft={draft}
        saving={saveMutation.isLoading}
        deleting={deleteMutation.isLoading}
        onArchive={archive}
        onClose={closeModal}
        onDelete={remove}
        onSave={save}
        onUpdate={updateDraft}
      />
    </div>
  );
}

export default LegalDocumentsPage;
